"use client";

import { useCallback, useEffect, useState, type ComponentProps } from "react";
import { FileRejection, useDropzone } from "react-dropzone";
import { Progress } from "@/components/ui/progress";
import toast from "@/lib/toast";
import { cn } from "@/lib/utils";
import { CheckCircleIcon } from "lucide-react";
import { v4 as uuidv4 } from "uuid";
import { client } from "@/lib/hono";
import { t } from "@/lib/localization";

export enum FileType {
    Images = "images",
    SVG = "svg",
    PDF = "pdf",
    CSV = "csv",
    Excel = "excel",
    Video = "video",
    Audio = "audio",
    Text = "text",
    Word = "word",
    PowerPoint = "powerpoint",
    Font = "font",
}

export const fileAcceptMapping = {
    [FileType.Images]: {
        "image/jpeg": [".jpeg", ".jpg"],
        "image/png": [".png"],
        "image/webp": [".webp"],
        "image/gif": [".gif"],
        "image/bmp": [".bmp"],
        "image/tiff": [".tiff"],
    },
    [FileType.SVG]: {
        "image/svg+xml": [".svg"],
    },
    [FileType.PDF]: {
        "application/pdf": [".pdf"],
    },
    [FileType.CSV]: {
        "text/csv": [".csv"],
    },
    [FileType.Excel]: {
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
            ".xlsx",
        ],
        "application/vnd.ms-excel": [".xls"],
    },
    [FileType.Video]: {
        "video/mp4": [".mp4"],
        "video/webm": [".webm"],
    },
    [FileType.Audio]: {
        "audio/mpeg": [".mp3"],
        "audio/wav": [".wav"],
        "audio/webm": [".webm"],
        "audio/ogg": [".ogg"],
        "audio/mp4": [".m4a"],
    },
    [FileType.Text]: {
        "text/plain": [".txt"],
    },
    [FileType.Word]: {
        "application/msword": [".doc"],
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
            [".docx"],
    },
    [FileType.PowerPoint]: {
        "application/vnd.ms-powerpoint": [".ppt"],
        "application/vnd.openxmlformats-officedocument.presentationml.presentation":
            [".pptx"],
    },
    [FileType.Font]: {
        "font/ttf": [".ttf"],
        "font/otf": [".otf"],
        "application/x-font-ttf": [".ttf"],
        "application/x-font-opentype": [".otf"],
        // Some browsers report font files as octet-stream — accept TTF/OTF only.
        "application/octet-stream": [".ttf", ".otf"],
    },
};

export type UploadFileType = {
    fileUrl: string;
    fileName: string;
    fileType?: string;
    fileSize?: number;
};

export interface UploadFileOptions {
    allowedFileTypes: FileType[];
    maxFileSize: number; // in MB
    fileUploadPath?: string;
    overrideFileName?: string;
    showImagePreview?: boolean;
    showSelectedFileName?: boolean;
    title?: { text: string; keyName: string };
    subtitle?: { text: string; keyName: string };
    className?: string;
    listenForInitialFileChanges?: boolean;
    maxImageWidth?: number;
    maxImageHeight?: number;
    purgeCache?: boolean;
    /** Override the preview <img> max-height (default `max-h-24`). */
    imagePreviewClassName?: string;
    /** Store as `name-<content hash>.ext`: a different file with the same name
     *  never overwrites another, and the same file always lands on the same URL. */
    uniqueFileName?: boolean;
    /** Applied last while a file is dragged over, so it beats `className`. */
    dragClassName?: string;
}

export interface UploadFileSingleProps {
    options: UploadFileOptions;
    multiple?: false;
    maxFiles?: never;
    initialFile?: UploadFileType | null;
    onUploadFinished: (uploadedFile: UploadFileType) => void;
    onUploadingChange?: (isUploading: boolean) => void;
}

interface UploadFileMultiProps {
    options: UploadFileOptions;
    multiple: true;
    maxFiles?: number;
    initialFile?: never;
    onUploadFinished: (uploadedFiles: UploadFileType[]) => void;
    onUploadingChange?: (isUploading: boolean) => void;
}

interface UploadFileParseProps {
    options: UploadFileOptions;
    parseOnly: true;
    onFileParsed: (file: File) => void;
    multiple?: false;
    maxFiles?: never;
    initialFile?: never;
    onUploadFinished?: never;
    onUploadingChange?: never;
}

type UploadFileProps =
    | UploadFileSingleProps
    | UploadFileMultiProps
    | UploadFileParseProps;

// Bigger files get a random id instead, so a large video is never read into memory.
const HASH_MAX_BYTES = 20 * 1024 * 1024;

async function contentId(file: File): Promise<string> {
    const randomId = () => uuidv4().slice(0, 8);
    if (file.size > HASH_MAX_BYTES || !globalThis.crypto?.subtle)
        return randomId();
    try {
        const digest = await crypto.subtle.digest(
            "SHA-256",
            await file.arrayBuffer()
        );
        return Array.from(new Uint8Array(digest).slice(0, 4), (b) =>
            b.toString(16).padStart(2, "0")
        ).join("");
    } catch {
        // Unreadable file: the upload itself fails next and reports it.
        return randomId();
    }
}

export async function withUniqueSuffix(file: File): Promise<string> {
    const id = await contentId(file);
    const dot = file.name.lastIndexOf(".");
    return dot > 0
        ? `${file.name.slice(0, dot)}-${id}${file.name.slice(dot)}`
        : `${file.name}-${id}`;
}

export interface XhrUploadControl {
    signal?: AbortSignal;
    stallMs?: number;
}

export function uploadFileViaXhr(
    file: File,
    signedUrl: string,
    onProgress: (percent: number) => void,
    { signal, stallMs }: XhrUploadControl = {}
): Promise<string> {
    return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        let stallTimer: ReturnType<typeof setTimeout> | undefined;
        const stop = (error: Error) => {
            clearTimeout(stallTimer);
            xhr.abort();
            reject(error);
        };
        const watchStall = () => {
            if (!stallMs) return;
            clearTimeout(stallTimer);
            stallTimer = setTimeout(
                () => stop(new Error("Upload stalled")),
                stallMs
            );
        };
        const onAbort = () => stop(new Error("Upload cancelled"));
        if (signal?.aborted) return reject(new Error("Upload cancelled"));
        if (signal || stallMs) {
            signal?.addEventListener("abort", onAbort, { once: true });
            xhr.addEventListener("loadend", () => {
                clearTimeout(stallTimer);
                signal?.removeEventListener("abort", onAbort);
            });
        }

        xhr.open("PUT", signedUrl, true);
        xhr.setRequestHeader("Content-Type", file.type);

        xhr.upload.addEventListener("progress", (event) => {
            watchStall();
            if (event.lengthComputable) {
                onProgress((event.loaded / event.total) * 100);
            }
        });

        xhr.addEventListener("error", (error) => {
            console.error(error);
            reject(new Error("Upload failed"));
        });

        xhr.addEventListener("load", () => {
            if (xhr.status >= 200 && xhr.status < 300) {
                resolve("ok");
            } else {
                console.error("Upload failed with status:", xhr.status);
                reject(new Error(`Upload failed with status: ${xhr.status}`));
            }
        });

        xhr.send(file);
        watchStall();
    });
}

export const dropzoneClassName = (
    isDragActive: boolean,
    className?: string,
    dragClassName?: string
) =>
    cn(
        "flex flex-col items-center justify-center p-4 border-2 border-dashed border-input rounded-md cursor-pointer text-center transition-colors",
        isDragActive
            ? "bg-muted border-muted-foreground/80"
            : "hover:bg-muted hover:border-muted-foreground/80",
        className && className,
        isDragActive && dragClassName
    );

export function UploadProgressBar({
    percent,
    ...props
}: { percent: number } & Omit<ComponentProps<typeof Progress>, "value">) {
    return (
        <Progress
            {...props}
            value={percent}
            aria-valuenow={Math.round(percent)}
            className="mt-2 h-1.5"
            indicatorClassName="rounded-full"
        />
    );
}

export default function UploadFile(props: UploadFileProps) {
    const {
        options: {
            allowedFileTypes,
            maxFileSize,
            fileUploadPath,
            overrideFileName,
            showImagePreview,
            showSelectedFileName = true,
            title = {
                text: t("upload-file.default.title"),
                keyName: "upload-file.default.title",
            },
            subtitle = {
                text: t("upload-file.default.subtitle"),
                keyName: "upload-file.default.subtitle",
            },
            className = "h-48 w-full",
            listenForInitialFileChanges,
            maxImageWidth,
            maxImageHeight,
            purgeCache,
            imagePreviewClassName,
            uniqueFileName,
            dragClassName,
        },
        onUploadFinished,
        onUploadingChange,
    } = props;

    const isMultiple = props.multiple === true;
    const initialFile = !isMultiple ? props.initialFile : undefined;
    const maxFiles = isMultiple ? props.maxFiles : undefined;
    const parseOnly = "parseOnly" in props && props.parseOnly === true;
    const onFileParsed =
        "onFileParsed" in props ? props.onFileParsed : undefined;

    const [uploadProgress, setUploadProgress] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [sizeLimitError, setShowSizeLimitError] = useState(false);
    const [currentFile, setCurrentFile] = useState<
        UploadFileType | null | undefined
    >(initialFile);
    const [currentFiles, setCurrentFiles] = useState<UploadFileType[]>([]);

    useEffect(() => {
        if (listenForInitialFileChanges && !isMultiple) {
            setCurrentFile(initialFile);
        }
    }, [initialFile, listenForInitialFileChanges, isMultiple]);

    useEffect(() => {
        onUploadingChange?.(isLoading);
    }, [isLoading, onUploadingChange]);

    const onDropRejected = useCallback((fileRejections: FileRejection[]) => {
        if (fileRejections.length > 0) {
            toast.error(t("toast.error.upload-file.type-not-supported"), {
                keyName: "toast.error.upload-file.type-not-supported",
            });
        }
    }, []);

    const onDropAccepted = useCallback(
        async (acceptedFiles: File[]) => {
            const downscaleImageIfNeeded = async (
                file: File,
                maxWidth: number,
                maxHeight: number
            ): Promise<File> => {
                console.log("downscaleImageIfNeeded was called");

                const img = new Image();
                const reader = new FileReader();

                return new Promise<File>((resolve, reject) => {
                    reader.onload = (e) => {
                        img.src = e.target?.result as string;
                    };

                    img.onload = () => {
                        const canvas = document.createElement("canvas");
                        let width = img.width;
                        let height = img.height;

                        if (img.width > maxWidth || img.height > maxHeight) {
                            console.log("downscaling image");

                            const aspectRatio = img.width / img.height;

                            if (img.width > img.height) {
                                width = maxWidth;
                                height = width / aspectRatio;
                            } else {
                                height = maxHeight;
                                width = height * aspectRatio;
                            }

                            canvas.width = width;
                            canvas.height = height;

                            const ctx = canvas.getContext("2d");
                            if (ctx) {
                                ctx.drawImage(img, 0, 0, width, height);
                            }

                            canvas.toBlob((blob) => {
                                if (blob) {
                                    const resizedFile = new File(
                                        [blob],
                                        file.name,
                                        {
                                            type: file.type,
                                            lastModified: Date.now(),
                                        }
                                    );
                                    resolve(resizedFile);
                                } else {
                                    resolve(file);
                                }
                            }, file.type);
                        } else {
                            console.log("no need to downscale");
                            resolve(file);
                        }
                    };

                    reader.onerror = reject;
                    reader.readAsDataURL(file);
                });
            };

            if (isLoading) {
                toast.error(t("toast.error.upload-file.already-uploading"), {
                    keyName: "toast.error.upload-file.already-uploading",
                });
                return;
            }

            if (!isMultiple && acceptedFiles.length > 1) {
                toast.error(
                    t("toast.error.upload-file.multiple-files-attempt"),
                    {
                        keyName: "toast.error.upload-file.multiple-files-attempt",
                    }
                );
                return;
            }

            if (!parseOnly && !fileUploadPath) {
                toast.error(t("toast.error.default"), {
                    keyName: "toast.error.default",
                });
                return;
            }

            // Validate file sizes
            const validFiles: File[] = [];
            let hasOversize = false;
            for (const file of acceptedFiles) {
                const fileSizeInMB = file.size / 1024 / 1024;
                if (fileSizeInMB > maxFileSize) {
                    hasOversize = true;
                    toast.error(
                        t("toast.error.file-upload.exceeded-max-file-size"),
                        {
                            keyName: "toast.error.file-upload.exceeded-max-file-size",
                        }
                    );
                } else {
                    validFiles.push(file);
                }
            }
            setShowSizeLimitError(hasOversize);

            if (validFiles.length === 0) return;

            if (parseOnly) {
                const file = validFiles[0];
                setCurrentFile({
                    fileName: file.name,
                    fileUrl: URL.createObjectURL(file),
                });
                onFileParsed?.(file);
                return;
            }

            setIsLoading(true);

            if (isMultiple) {
                // --- Multi-file upload flow ---
                const localFiles = validFiles.map((f) => ({
                    fileName: f.name,
                    fileUrl: URL.createObjectURL(f),
                }));
                setCurrentFiles(localFiles);

                const storedNames = uniqueFileName
                    ? await Promise.all(validFiles.map(withUniqueSuffix))
                    : validFiles.map((f) => f.name);

                const res = await client.api.storage["file-upload"].$post({
                    json: {
                        files: validFiles.map((f, i) => ({
                            filePath: `${fileUploadPath}/${storedNames[i]}`,
                            contentType: f.type,
                            purgeCache,
                            bucket: "public" as const,
                        })),
                    },
                });

                if (!res.ok) {
                    toast.error(t("toast.error.unexpected"), {
                        keyName: "toast.error.unexpected",
                    });
                    setIsLoading(false);
                    return;
                }

                const { signedUrls } = await res.json();

                // Track per-file progress for aggregate
                const progresses = new Array(validFiles.length).fill(0);
                const updateAggregateProgress = () => {
                    const total = progresses.reduce((sum, p) => sum + p, 0);
                    setUploadProgress(total / progresses.length);
                };

                const results: UploadFileType[] = [];
                const uploadPromises = validFiles.map(async (file, i) => {
                    const { signedUrl, publicUrl } = signedUrls[i];
                    try {
                        await uploadFileViaXhr(file, signedUrl, (percent) => {
                            progresses[i] = percent;
                            updateAggregateProgress();
                        });
                        results.push({
                            fileName: file.name,
                            fileType: file.type,
                            fileSize: file.size,
                            fileUrl: publicUrl!,
                        });
                    } catch {
                        toast.error(t("toast.error.upload-file.failed"), {
                            keyName: "toast.error.upload-file.failed",
                        });
                    }
                });

                await Promise.all(uploadPromises);

                if (results.length > 0) {
                    (onUploadFinished as (files: UploadFileType[]) => void)(results);
                    toast.success(t("toast.success.upload-file"), {
                        keyName: "toast.success.upload-file",
                    });
                }

                setUploadProgress(0);
                setIsLoading(false);
            } else {
                // --- Single-file upload flow (unchanged behavior) ---
                const file = validFiles[0];
                const localUrl = URL.createObjectURL(file);
                setCurrentFile({ fileName: file.name, fileUrl: localUrl });

                const storedName =
                    overrideFileName ||
                    (uniqueFileName ? await withUniqueSuffix(file) : file.name);

                const res = await client.api.storage["file-upload"].$post({
                    json: {
                        files: [
                            {
                                filePath: `${fileUploadPath}/${storedName}`,
                                contentType: file.type,
                                purgeCache,
                                bucket: "public",
                            },
                        ],
                    },
                });

                if (!res.ok) {
                    toast.error(t("toast.error.unexpected"), {
                        keyName: "toast.error.unexpected",
                    });
                    setIsLoading(false);
                    return;
                }

                let processedFile = file;
                if (maxImageWidth && maxImageHeight) {
                    processedFile = await downscaleImageIfNeeded(
                        file,
                        maxImageWidth,
                        maxImageHeight
                    );
                }

                const { signedUrls } = await res.json();
                const [{ signedUrl, publicUrl }] = signedUrls;

                try {
                    await uploadFileViaXhr(
                        processedFile,
                        signedUrl,
                        (percent) => setUploadProgress(percent)
                    );

                    (onUploadFinished as (file: UploadFileType) => void)({
                        fileName: file.name,
                        fileType: file.type,
                        fileSize: file.size,
                        fileUrl: publicUrl!,
                    });

                    toast.success(t("toast.success.upload-file"), {
                        keyName: "toast.success.upload-file",
                    });
                    setUploadProgress(0);
                } catch {
                    toast.error(t("toast.error.upload-file.failed"), {
                        keyName: "toast.error.upload-file.failed",
                    });
                }
                setIsLoading(false);
            }
        },
        [
            fileUploadPath,
            isLoading,
            isMultiple,
            maxFileSize,
            maxImageHeight,
            maxImageWidth,
            onUploadFinished,
            overrideFileName,
            purgeCache,
            parseOnly,
            uniqueFileName,
            onFileParsed,
        ]
    );

    const accept = allowedFileTypes.reduce((acc, type) => {
        return { ...acc, ...fileAcceptMapping[type] };
    }, {});

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDropAccepted,
        onDropRejected,
        accept,
        multiple: isMultiple,
        ...(maxFiles ? { maxFiles } : {}),
    });

    return (
        <div
            {...getRootProps()}
            className={dropzoneClassName(
                isDragActive,
                className,
                dragClassName
            )}
        >
            <input {...getInputProps()} />
            {isMultiple && currentFiles.length > 0 ? (
                <div className="text-wrap overflow-hidden w-full flex flex-col items-center gap-1">
                    {!isLoading && (
                        <CheckCircleIcon className="h-6 w-6 text-green-500" />
                    )}
                    {showSelectedFileName && (
                        <>
                            <p className="font-semibold text-sm mt-1">
                                {t("upload-file.selected-files")}
                            </p>
                            {currentFiles.map((f, i) => (
                                <p key={i} className="text-xs">
                                    {f.fileName}
                                </p>
                            ))}
                        </>
                    )}
                </div>
            ) : currentFile ? (
                <div className="text-wrap overflow-hidden w-full flex flex-col items-center">
                    {!isLoading && !showImagePreview && (
                        <CheckCircleIcon className="h-6 w-6 text-green-500" />
                    )}

                    {showImagePreview && currentFile.fileUrl && (
                        // eslint-disable-next-line @next/next/no-img-element
                        <img
                            src={currentFile.fileUrl}
                            alt={t("upload-file.preview.alt")}
                            className={cn(
                                imagePreviewClassName ?? "max-h-24",
                                "max-w-full mb-1 rounded-md shadow-sm",
                                isDragActive ? "opacity-70" : "opacity-100"
                            )}
                        />
                    )}
                    {showSelectedFileName && (
                        <>
                            <p className="font-semibold text-sm mt-1">
                                {t("upload-file.selected-file")}
                            </p>
                            <p className="text-xs mt-1">
                                {currentFile.fileName}
                            </p>
                        </>
                    )}
                </div>
            ) : (
                <div>
                    <p className="font-semibold text-sm">
                        {t(title.keyName, undefined, undefined)}
                    </p>
                    <p className="text-xs mt-0.5">
                        {t(subtitle.keyName, undefined, undefined)}
                    </p>
                </div>
            )}
            {isLoading && <UploadProgressBar percent={uploadProgress} />}
            {sizeLimitError && (
                <div>
                    <p className="text-destructive text-sm font-semibold mt-1">
                        {t("upload-file.max-file-size-exceeded-error.text", undefined, undefined)}
                    </p>
                </div>
            )}
        </div>
    );
}
